import { useMutation } from '@tanstack/react-query'
import { CHAIN_CONFIG, CHAIN_INFO_LIST } from '@/config'
import {
  generateIbcTimeoutTimestampBigInt,
  calculateFeeWithMinimalDenom,
  isSelectedCoinMinimalDenom
} from '@/wallet-utils'
import { assert } from '@/utils'
import { MutationParameters } from './types'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { useStake } from '../../StakeProvider'
import { useSelectedChain, useStrideChain } from '@/contexts/wallet'
import { flushSync } from 'react-dom'
import { ibc } from 'stridejs'
import { useSelectedDenom } from '../../../../contexts/denom'

const useSignWithdrawTokenizedSharesMutation = ({ setWithdrawStTokenRaw }: MutationParameters) => {
  const { address: strideAddress, getSigningStargateClient } = useStrideChain()

  const { address: selectedAddress } = useSelectedChain()

  const [denom] = useSelectedDenom()

  const { withdrawLsm } = useStake()

  const handleMutation = async (): Promise<TxRaw> => {
    assert(strideAddress, 'You are unable to revert tokenized shares without connecting your Stride wallet.')

    assert(selectedAddress, `You are unable to revert tokenized shares without connecting your ${denom} wallet.`)

    const sourceChannel = CHAIN_CONFIG[denom].withdrawChannel

    const transfer = ibc.applications.transfer.v1.MessageComposer.withTypeUrl.transfer({
      sourcePort: 'transfer',
      sourceChannel: sourceChannel,
      token: {
        amount: withdrawLsm.amount,
        denom: withdrawLsm.txHistoryIbcDenom
      },
      sender: strideAddress,
      receiver: selectedAddress,
      memo: '',
      timeoutHeight: {
        revisionHeight: 0n,
        revisionNumber: 0n
      },
      timeoutTimestamp: generateIbcTimeoutTimestampBigInt()
    })

    const minimalDenom = CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom

    assert(isSelectedCoinMinimalDenom(minimalDenom), `Invalid minimal denom for ${denom}`)

    const client = await getSigningStargateClient()
    const gas = await client.simulate(strideAddress, [transfer], '')
    const fee = calculateFeeWithMinimalDenom(minimalDenom, gas)

    return await client.sign(strideAddress, [transfer], fee, '')
  }

  const handleSuccess = (raw: TxRaw) => {
    flushSync(() => {
      setWithdrawStTokenRaw(raw)
    })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignWithdrawTokenizedSharesMutation }
